import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import {
  Box,
  Button,
  Modal,
  Fade,
  Backdrop,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { Loader } from '@/components/loader/loader';
import { AppDataGrid } from '@/components/ui';
import { UserDto, PermissionArea } from '@cloudcost/shared-types/users';
import { hasUserAccessTo } from '@/lib/permissions';
import { AddUserDialog } from './cloud-accounts/AddUserDialog';
import { CloudAccountFormData, Provider } from './cloud-accounts/types';
import { CloudAccountForm } from './cloud-accounts/CloudAccountForm';
import { AssignedTechUsers } from './cloud-accounts/AssignedTechUsers';
import { DeleteUserDialog } from './cloud-accounts/DeleteUserDialog';
import { SyncAccountDialog } from './cloud-accounts/SyncAccountDialog';
import {
  SyncDateRange,
  isValidSyncDateRange,
  toSyncDatePayload,
} from './sync-date-range';

const StyledModal = styled(Modal)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const baseColumns = [
  { field: 'provider_name', headerName: 'Provider', flex: 0.5 },
  { field: 'name', headerName: 'Tenant Name', flex: 0.5 },
  { 
    field: 'account', 
    headerName: 'Account Details', 
    flex: 1.5,
    renderCell: (params: any) => {
      const row = params.row;
      if (row.provider_name === 'Azure') {
        return `Client ID: ${row.account || 'N/A'}`;
      } else if (row.provider_name === 'AWS') {
        return `Access Key: ${row.account || 'N/A'}`;
      }
      return row.account || 'N/A';
    }
  },
  { 
    field: 'provider_specific', 
    headerName: 'Provider Specific', 
    flex: 1.5,
    renderCell: (params: any) => {
      const row = params.row;
      if (row.provider_name === 'Azure') {
        return `Tenant ID: ${row.tenant_id || 'N/A'}`;
      } else if (row.provider_name === 'AWS') {
        return `Master Account: ${row.master_account || 'N/A'}`;
      }
      return 'N/A';
    }
  },
];

export default function Accounts() {
  const isAdmin = hasUserAccessTo(PermissionArea.ADMIN_PANEL);
  const canEditAccount = isAdmin;
  const canManageOwner = isAdmin;
  const [alertProvider, setAlertProvider] = useState(false);
  const [alertTenantName, setAlertTenantName] = useState(false);
  const [alertAccount, setAlertAccount] = useState(false);
  const [alertSecret, setAlertSecret] = useState(false);
  const [alertTenantId, setAlertTenantId] = useState(false);
  const [alertOwner, setAlertOwner] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [accountValue, setAccountValue] = useState<CloudAccountFormData>({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignedTechUsers, setAssignedTechUsers] = useState<UserDto[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserDto[]>([]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [deleteUserConfirmation, setDeleteUserConfirmation] = useState(false);
  const [selectedUserToDelete, setSelectedUserToDelete] =
    useState<UserDto | null>(null);
  const [syncingAccountId, setSyncingAccountId] = useState<number | null>(null);
  const [syncDialogAccount, setSyncDialogAccount] = useState<{
    id: number;
    name: string;
    provider_name: string;
  } | null>(null);
  const [syncDateRange, setSyncDateRange] = useState<SyncDateRange>({
    dateFrom: null,
    dateTo: null,
  });

  const openSyncDialog = useCallback((row: { id: number; name: string; provider_name: string }) => {
    setSyncDialogAccount(row);
  }, []);

  const closeSyncDialog = useCallback(() => {
    if (syncingAccountId != null) return;
    setSyncDialogAccount(null);
  }, [syncingAccountId]);

  const handleSyncAccount = useCallback(async () => {
    if (!syncDialogAccount) return;
    if (!isValidSyncDateRange(syncDateRange)) {
      toast.error('Date from must be before date to');
      return;
    }
    setSyncingAccountId(syncDialogAccount.id);
    try {
      const res = await fetchWrapper.post(urls.DevOps.syncCloudAccount, {
        accountID: syncDialogAccount.id,
        ...toSyncDatePayload(syncDateRange),
      });
      toast.success(res.message ?? 'Synchronization scheduled');
      setSyncDialogAccount(null);
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to schedule synchronization');
    } finally {
      setSyncingAccountId(null);
    }
  }, [syncDateRange, syncDialogAccount]);

  const columns = useMemo(
    () => [
      ...baseColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.6,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
          <Button
            variant="outlined"
            size="small"
            disabled={syncingAccountId === params.row.id}
            onClick={(event) => {
              event.stopPropagation();
              openSyncDialog({
                id: params.row.id,
                name: params.row.name,
                provider_name: params.row.provider_name,
              });
            }}
            data-testid={`cloud-account-sync-${params.row.id}`}>
            {syncingAccountId === params.row.id ? 'Syncing…' : 'Sync'}
          </Button>
        ),
      },
    ],
    [openSyncDialog, syncingAccountId],
  );

  useEffect(() => {
    setLoading(true);

    if (isAdmin) {
      fetchWrapper
        .get(urls.DevOps.getCloudProviders)
        .then((data) => setProviders(data))
        .catch((error) => console.error('Error fetching providers', error));

      fetchWrapper
        .get(urls.DevOps.getUsersTech)
        .then((data) => setUsers(data))
        .catch((error) => console.error('Error fetching users', error));
    }

    fetchWrapper
      .get(urls.DevOps.getCloudAccounts)
      .then((data) => setItems(data))
      .catch((error) => console.error('Error fetching accounts', error))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const handleOpen2 = () => {
    setAccountValue({ new: true });
    setOpen(true);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const fetchAssignedTechUsers = async (cloudAccountId: number) => {
    try {
      const data = await fetchWrapper.get(
        urls.DevOps.getCloudAccountTechUsers(cloudAccountId),
      );
      setAssignedTechUsers(data);
    } catch (error) {
      console.error('Error fetching assigned tech users', error);
      setAssignedTechUsers([]);
    }
  };

  const fetchAvailableUsers = async (cloudAccountId: number) => {
    try {
      const data = await fetchWrapper.get(
        urls.DevOps.getAvailableCloudAccountTechUsers(cloudAccountId),
      );
      setAvailableUsers(data);
    } catch (error) {
      console.error('Error fetching available users', error);
      setAvailableUsers([]);
    }
  };

  const handleClose = () => {
    setAlertProvider(false);
    setAlertTenantName(false);
    setAlertAccount(false);
    setAlertSecret(false);
    setAlertTenantId(false);
    setAlertOwner(false);
    setOpen(false);
    setAccountValue({});
    setAssignedTechUsers([]);
    setAvailableUsers([]);
    setAddUserOpen(false);
    setDeleteUserConfirmation(false);
    setSelectedUserToDelete(null);
  };

  const openConfirmation = () => setConfirmation(true);
  const closeConfirmation = () => setConfirmation(false);

  const handleAddUser = async (userId: number) => {
    try {
      await fetchWrapper.post(
        urls.DevOps.addCloudAccountTechUser(accountValue.id!),
        {
          user_id: userId,
        },
      );
      await fetchAssignedTechUsers(accountValue.id!);
      await fetchAvailableUsers(accountValue.id!);
      setAddUserOpen(false);
    } catch (error) {
      console.error('Error adding tech user', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserToDelete) return;

    try {
      await fetchWrapper.delete(
        urls.DevOps.deleteCloudAccountTechUser(
          accountValue.id!,
          selectedUserToDelete.id,
        ),
      );
      await fetchAssignedTechUsers(accountValue.id!);
      await fetchAvailableUsers(accountValue.id!);
      setDeleteUserConfirmation(false);
      setSelectedUserToDelete(null);
    } catch (error) {
      console.error('Error deleting tech user', error);
    }
  };

  const openDeleteUserConfirmation = (user: UserDto) => {
    setSelectedUserToDelete(user);
    setDeleteUserConfirmation(true);
  };

  const closeDeleteUserConfirmation = () => {
    setDeleteUserConfirmation(false);
    setSelectedUserToDelete(null);
  };

  const deleteAccount = () => {
    closeConfirmation();
    handleClose();
    fetchWrapper
      .post(urls.DevOps.deleteCloudAccount, { id: accountValue.id })
      .then(() => {
        fetchWrapper
          .get(urls.DevOps.getCloudAccounts)
          .then((data) => setItems(data))
          .catch((error) => console.error('Error fetching accounts', error));
      });
  };

  const createAccount = () => {
    setAlertProvider(false);
    setAlertTenantName(false);
    setAlertAccount(false);
    setAlertSecret(false);
    setAlertTenantId(false);
    setAlertOwner(false);

    let alertSet = false;

    if (!accountValue.provider_id) {
      setAlertProvider(true);
      alertSet = true;
    }

    if (!accountValue.name) {
      setAlertTenantName(true);
      alertSet = true;
    }

    if (!accountValue.account) {
      setAlertAccount(true);
      alertSet = true;
    }

    if (!accountValue.secret) {
      setAlertSecret(true);
      alertSet = true;
    }

    // Provider-specific validation
    const selectedProvider = providers.find((row) => row.id === accountValue.provider_id);
    if (selectedProvider?.name === 'Azure' && !accountValue.tenant_id) {
      setAlertTenantId(true);
      alertSet = true;
    }

    // For AWS, validate required fields if this is a new account or if provider was changed
    if (selectedProvider?.name === 'AWS') {
      if (!accountValue.master_account || !accountValue.report_name || !accountValue.bucket || !accountValue.region) {
        // Only show error if this is a new account or if we're missing critical AWS fields
        if (accountValue.new || !accountValue.master_account || !accountValue.bucket) {
          // For now, we'll just show a general error - in a real app you might want specific field validation
          console.warn('AWS account missing required fields');
        }
      }
    }

    if (!accountValue.owner_id) {
      setAlertOwner(true);
      alertSet = true;
    }

    if (alertSet) {
      return;
    }

    const requestData = {
      provider_id: accountValue.provider_id,
      account: accountValue.account,
      secret: accountValue.secret,
      name: accountValue.name,
      owner_id: accountValue.owner_id,
      // Azure-specific fields
      tenant_id: accountValue.tenant_id,
      // AWS-specific fields
      master_account: accountValue.master_account,
      report_name: accountValue.report_name,
      report_prefix: accountValue.report_prefix,
      bucket: accountValue.bucket,
      region: accountValue.region,
    };

    if (accountValue.new !== true) {
      fetchWrapper
        .post(urls.DevOps.upsertCloudAccount, {
          id: accountValue.id,
          ...requestData,
        })
        .then(() => {
          fetchWrapper
            .get(urls.DevOps.getCloudAccounts)
            .then((data) => setItems(data))
            .catch((error) => console.error('Error fetching accounts', error));
        });
    } else {
      fetchWrapper
        .post(urls.DevOps.upsertCloudAccount, requestData)
        .then(() => {
          fetchWrapper
            .get(urls.DevOps.getCloudAccounts)
            .then((data) => setItems(data))
            .catch((error) => console.error('Error fetching accounts', error));
        });
    }

    handleClose();
  };

  const handleAddUserClick = () => {
    fetchAvailableUsers(accountValue.id!);
    setAddUserOpen(true);
  };

  if (loading) {
    return <Loader />;
  }

  const alerts = {
    alertProvider,
    alertTenantName,
    alertAccount,
    alertSecret,
    alertTenantId,
    alertOwner,
  };

  return (
    <div style={{ height: '100%' }}>
      <AppDataGrid
        style={{ height: '450px' }}
        rows={items}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5, page: 0 },
          },
        }}
        onCellClick={(clickedAccount) => {
          if (clickedAccount.field === 'actions') return;
          handleOpen();
          setAccountValue(clickedAccount.row);
          if (clickedAccount.row.id) {
            fetchAssignedTechUsers(clickedAccount.row.id);
          }
        }}
      />

      {isAdmin && (
        <Button
          variant="contained"
          onClick={handleOpen2}
          color="primary"
          style={{ float: 'right', marginTop: '10px', marginBottom: '10px' }}
          data-testid="cloud-accounts-add">
          Add
        </Button>
      )}

      <StyledModal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}>
        <Fade in={open}>
          <StyledDialogBox
            data-testid="cloud-account-dialog"
            sx={{
              width: accountValue.new ? 400 : 900,
              bgcolor: 'background.paper',
              boxShadow: '0 24px 60px rgba(16,35,58,.22)',
              p: 4,
            }}>
            <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
              {accountValue.new
                ? 'Add New Cloud Account'
                : 'Edit Cloud Account'}
            </Typography>

            {accountValue.new ? (
              <CloudAccountForm
                accountValue={accountValue}
                setAccountValue={setAccountValue}
                providers={providers}
                users={users}
                canManageOwner={canManageOwner}
                canEditAccount={canEditAccount}
                alerts={alerts}
                onCreateAccount={createAccount}
                onDeleteAccount={openConfirmation}
              />
            ) : (
              <Grid container spacing={4} sx={{ width: 'calc(100% + 96px)' }}>
                <Grid item xs={5}>
                  <CloudAccountForm
                    accountValue={accountValue}
                    setAccountValue={setAccountValue}
                    providers={providers}
                    users={users}
                    canManageOwner={canManageOwner}
                    canEditAccount={canEditAccount}
                    alerts={alerts}
                    onCreateAccount={createAccount}
                    onDeleteAccount={openConfirmation}
                  />
                </Grid>
                <Grid item xs={6}>
                  <AssignedTechUsers
                    cloudAccountId={accountValue.id!}
                    assignedTechUsers={assignedTechUsers}
                    onAddUser={handleAddUserClick}
                    onDeleteUser={openDeleteUserConfirmation}
                  />
                </Grid>
              </Grid>
            )} 
          </StyledDialogBox>
        </Fade>
      </StyledModal>

      <Dialog
        open={confirmation}
        onClose={closeConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this cloud account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone. All account data and history will be
            permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation}>Cancel</Button>
          <Button onClick={deleteAccount} autoFocus data-testid="confirmation-dialog-confirm">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <AddUserDialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        users={availableUsers}
        onSelectUser={handleAddUser}
      />

      <DeleteUserDialog
        open={deleteUserConfirmation}
        onClose={closeDeleteUserConfirmation}
        user={selectedUserToDelete}
        onConfirm={handleDeleteUser}
      />

      <SyncAccountDialog
        open={syncDialogAccount != null}
        accountName={syncDialogAccount?.name ?? null}
        providerName={syncDialogAccount?.provider_name ?? null}
        syncing={syncDialogAccount != null && syncingAccountId === syncDialogAccount.id}
        dateRange={syncDateRange}
        onDateRangeChange={setSyncDateRange}
        onClose={closeSyncDialog}
        onConfirm={() => void handleSyncAccount()}
      />
    </div>
  );
}

const StyledDialogBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: 'var(--cc-radius)',
}));
